import { z } from 'zod';
import { phoneSchema } from '../utils/validation';

/**
 * GSTIN (Goods and Services Tax Identification Number) validation regex.
 * Format: 2-digit state code + 10-char PAN + 1 entity code + Z + 1 checksum
 * Example: 27AAPFU0939F1ZV
 */
export const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/;

/**
 * Validates whether a string is a valid GSTIN.
 * Returns false for null, undefined, empty string, or invalid format.
 */
export function isValidGSTIN(gstin: string | null | undefined): boolean {
  if (!gstin || typeof gstin !== 'string') return false;
  return GSTIN_REGEX.test(gstin.trim());
}

/**
 * Staff permissions enum matching types/user.ts StaffPermission
 */
export const StaffPermissionEnum = z.enum([
  'VIEW_BOOKINGS',
  'MANAGE_BOOKINGS',
  'VIEW_REVENUE',
  'MANAGE_AVAILABILITY',
  'MANAGE_VENUE_DETAILS',
  'VIEW_REVIEWS',
  'RESPOND_TO_REVIEWS',
]);

export type StaffPermission = z.infer<typeof StaffPermissionEnum>;

/**
 * Business type enum
 */
export const BusinessTypeEnum = z.enum(['GAMING', 'SNOOKER', 'KIDS_PLAYAREA']);

export type BusinessType = z.infer<typeof BusinessTypeEnum>;

/**
 * GST Registration Type enum
 */
export const GSTRegistrationTypeEnum = z.enum(['REGULAR', 'COMPOSITION', 'UNREGISTERED']);

export type GSTRegistrationType = z.infer<typeof GSTRegistrationTypeEnum>;

/**
 * Update vendor schema
 *
 * Used by: PUT /vendors/:id, PUT /vendors/admin/:id
 */
export const UpdateVendorSchema = z.object({
  businessName: z.string().min(3, 'Business name must be at least 3 characters').optional(),
  description: z.string().optional(),
  logo: z.string().url('Logo must be a valid URL').optional(),
  phone: phoneSchema.optional(),
  instagram: z
    .string()
    .regex(
      /^@?[a-zA-Z0-9_.]{1,30}$/,
      'Invalid Instagram handle (letters, numbers, underscores and dots only, max 30 chars)'
    )
    .or(z.literal(''))
    .optional(),
  // GST and legal fields
  gstin: z.string().optional(),
  pan: z.string().optional(),
  legalBusinessName: z.string().optional(),
  registeredAddress: z.string().optional(),
  state: z.string().optional(),
  gstRegistrationType: GSTRegistrationTypeEnum.optional(),
  companyLicense: z.string().optional(),
  fssaiLicenseNumber: z
    .string()
    .regex(/^\d{14}$/, 'FSSAI license number must be exactly 14 digits')
    .or(z.literal(''))
    .optional(),
});

export type UpdateVendorRequest = z.infer<typeof UpdateVendorSchema>;

/**
 * Vendor onboarding schema (complete registration)
 *
 * Used by: POST /vendors/onboard
 */
export const VendorOnboardingSchema = z.object({
  businessName: z.string().min(3, 'Business name must be at least 3 characters'),
  description: z.string().min(20, 'Description must be at least 20 characters'),
  businessType: BusinessTypeEnum,
  phone: phoneSchema,
  address: z.string().min(10, 'Address must be at least 10 characters'),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  logo: z.string().url('Logo must be a valid URL').optional(),
  documents: z.array(z.string().url()).optional(),
});

export type VendorOnboardingRequest = z.infer<typeof VendorOnboardingSchema>;

/**
 * Vendor approval schema (admin action)
 *
 * Used by: PUT /vendors/:id/approve
 */
export const VendorApprovalSchema = z
  .object({
    approve: z.boolean(),
    reason: z.string().optional(),
  })
  .refine(
    (data) =>
      data.approve === true ||
      (data.approve === false && !!data.reason && data.reason.length >= 10),
    {
      message: 'Rejection reason must be at least 10 characters',
      path: ['reason'],
    }
  );

export type VendorApprovalRequest = z.infer<typeof VendorApprovalSchema>;

/**
 * Invite staff schema
 *
 * Used by: POST /vendors/:id/staff/invite
 */
export const InviteStaffSchema = z.object({
  email: z.string().email('Invalid email format'),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  permissions: z.array(StaffPermissionEnum).min(1, 'At least one permission is required'),
  assignedVenues: z.array(z.string().uuid()).optional().default([]),
});

export type InviteStaffRequest = z.infer<typeof InviteStaffSchema>;

/**
 * Update staff permissions schema
 *
 * Used by: PUT /vendors/:vendorId/staff/:staffId
 */
export const UpdateStaffSchema = z.object({
  permissions: z
    .array(StaffPermissionEnum)
    .min(1, 'At least one permission is required')
    .optional(),
  assignedVenues: z.array(z.string().uuid()).optional(),
  isActive: z.boolean().optional(),
});

export type UpdateStaffRequest = z.infer<typeof UpdateStaffSchema>;

/**
 * Vendor list query params
 *
 * Used by: GET /vendors
 */
export const VendorListQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  search: z.string().optional(),
  isVerified: z
    .string()
    .transform((v) => v === 'true')
    .optional(),
  businessType: BusinessTypeEnum.optional(),
});

export type VendorListQuery = z.infer<typeof VendorListQuerySchema>;

/**
 * Vendor enquiry schema (public venue listing enquiry)
 *
 * Used by: POST /vendors/apply
 */
export const ApplyVendorSchema = z.object({
  contactName: z
    .string()
    .min(1, 'Contact name is required')
    .max(100, 'Contact name must be less than 100 characters'),
  email: z.string().email('Invalid email format'),
  phone: z
    .string()
    .regex(/^[6-9]\d{9}$/, 'Phone number must be a valid 10-digit Indian mobile number'),
  venueName: z
    .string()
    .min(1, 'Venue name is required')
    .max(200, 'Venue name must be less than 200 characters'),
  address: z
    .string()
    .min(1, 'Address is required')
    .max(500, 'Address must be less than 500 characters'),
  city: z.string().min(1, 'City is required').max(100, 'City must be less than 100 characters'),
});

export type ApplyVendorRequest = z.infer<typeof ApplyVendorSchema>;

/**
 * Admin create vendor schema (admin creates vendor with user account)
 *
 * Used by: POST /vendors/admin/create
 *
 * Accepts either `name` directly, or `firstName` + `lastName` which are combined into `name`.
 */
export const AdminCreateVendorSchema = z
  .object({
    // User account fields
    email: z.string().email('Invalid email format'),
    name: z.string().min(2, 'Name must be at least 2 characters').optional(),
    firstName: z.string().min(1, 'First name is required').optional(),
    lastName: z.string().min(1, 'Last name is required').optional(),
    phone: phoneSchema.optional(),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      )
      .optional(),
    // Vendor business fields
    businessName: z.string().min(3, 'Business name must be at least 3 characters'),
    description: z.string().optional(),
    logo: z.string().url('Logo must be a valid URL').optional(),
    // Admin control
    isVerified: z.boolean().optional().default(true),
  })
  .superRefine((data, ctx) => {
    // Require either name or firstName+lastName
    if (!data.name && (!data.firstName || !data.lastName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Either name or both firstName and lastName must be provided',
        path: ['name'],
      });
    }
  })
  .transform((data) => {
    const { firstName, lastName, name, ...rest } = data;
    return {
      ...rest,
      name: name ?? `${firstName} ${lastName}`.trim(),
    };
  });

export type AdminCreateVendorRequest = z.infer<typeof AdminCreateVendorSchema>;

/**
 * Pending vendors query params
 *
 * Used by: GET /vendors/pending
 */
export const PendingVendorsQuerySchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
});

export type PendingVendorsQuery = z.infer<typeof PendingVendorsQuerySchema>;

/**
 * Vendor ID path parameter
 *
 * Used by: GET /vendors/:id, PUT /vendors/:id, DELETE /vendors/:id, PUT /vendors/:id/approve, PUT /vendors/admin/:id
 */
export const VendorIdParamSchema = z.object({
  id: z.string().uuid('Invalid vendor ID format'),
});

export type VendorIdParam = z.infer<typeof VendorIdParamSchema>;

/**
 * Update vendor notification settings schema
 *
 * Used by: PUT /vendors/me/notification-settings
 */
export const UpdateVendorNotificationSettingsSchema = z.object({
  emailEnabled: z.boolean().optional(),
  pushEnabled: z.boolean().optional(),
  smsEnabled: z.boolean().optional(),
  whatsappEnabled: z.boolean().optional(),
  typePreferences: z.record(z.any()).optional(),
});

export type UpdateVendorNotificationSettingsRequest = z.infer<
  typeof UpdateVendorNotificationSettingsSchema
>;

// ============================================================
// Vendor Settings Schemas (SETT-01 to SETT-07)
// ============================================================

/**
 * Update vendor profile schema (SETT-01)
 *
 * Used by: PATCH /vendors/me/profile
 */
export const UpdateVendorProfileSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  businessName: z.string().min(1, 'Business name is required').max(200, 'Business name too long'),
  contactEmail: z.string().email('Invalid email format'),
  contactPhone: z
    .string()
    .regex(/^\+?[1-9]\d{9,14}$/, 'Invalid phone number (10-15 digits)')
    .optional()
    .or(z.literal('')),
});

export type UpdateVendorProfile = z.infer<typeof UpdateVendorProfileSchema>;

/**
 * Update business info schema (SETT-02)
 *
 * Used by: PATCH /vendors/me/business-info
 */
export const UpdateBusinessInfoSchema = z.object({
  gstNumber: z
    .string()
    .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/, 'Invalid GST number format')
    .optional()
    .or(z.literal('')),
  panNumber: z
    .string()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Invalid PAN number format')
    .optional()
    .or(z.literal('')),
  businessAddress: z
    .object({
      line1: z.string().min(1, 'Address line 1 is required').max(200),
      line2: z.string().max(200).optional(),
      city: z.string().min(1, 'City is required').max(100),
      state: z.string().min(1, 'State is required').max(100),
      pincode: z.string().regex(/^\d{6}$/, 'Invalid pincode (6 digits)'),
    })
    .optional(),
});

export type UpdateBusinessInfo = z.infer<typeof UpdateBusinessInfoSchema>;

/**
 * Update bank account schema (SETT-03)
 *
 * Used by: PATCH /vendors/me/bank-account
 */
export const UpdateBankAccountSchema = z.object({
  accountHolderName: z.string().min(1, 'Account holder name required').max(100),
  accountNumber: z.string().regex(/^\d{9,18}$/, 'Invalid account number (9-18 digits)'),
  ifscCode: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code'),
  bankName: z.string().min(1, 'Bank name required').max(100),
});

export type UpdateBankAccount = z.infer<typeof UpdateBankAccountSchema>;

/**
 * Notification preferences schema (SETT-04)
 *
 * Used by: PATCH /vendors/me/notification-preferences
 */
export const NotificationPreferencesSchema = z.object({
  emailNotifications: z.object({
    bookings: z.boolean(),
    payouts: z.boolean(),
    reviews: z.boolean(),
    marketing: z.boolean(),
  }),
  smsNotifications: z.object({
    bookings: z.boolean(),
    payouts: z.boolean(),
  }),
});

export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

/**
 * Change password schema (SETT-05)
 *
 * Used by: PATCH /vendors/me/change-password
 */
export const ChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain uppercase letter')
      .regex(/[a-z]/, 'Password must contain lowercase letter')
      .regex(/[0-9]/, 'Password must contain a number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export type ChangePassword = z.infer<typeof ChangePasswordSchema>;

/**
 * Email verification schema (SETT-06)
 *
 * Used by: POST /vendors/me/verify-email
 */
export const EmailVerificationSchema = z.object({
  email: z.string().email('Invalid email format'),
  token: z.string().min(1, 'Verification token required').optional(),
});

export type EmailVerification = z.infer<typeof EmailVerificationSchema>;

/**
 * Profile photo metadata schema (SETT-07) - for backend file validation
 *
 * Used by: POST /vendors/me/profile-photo
 */
export const ProfilePhotoMetaSchema = z.object({
  filename: z.string().min(1),
  mimetype: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  size: z.number().max(5 * 1024 * 1024, 'File must be under 5MB'),
});

export type ProfilePhotoMeta = z.infer<typeof ProfilePhotoMetaSchema>;

// ============================================================
// Vendor Onboarding State Schemas
// ============================================================

/**
 * Vendor onboarding state response schema
 *
 * Returned by: GET /vendors/me/onboarding-state
 */
export const VendorOnboardingStateSchema = z.object({
  id: z.string().nullable(),
  vendorId: z.string(),
  welcomeDismissed: z.boolean(),
  checklistDismissed: z.boolean(),
  completedSteps: z.array(z.string()),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
});

export type VendorOnboardingStateResponse = z.infer<typeof VendorOnboardingStateSchema>;

/**
 * Patch vendor onboarding state schema
 *
 * Used by: PATCH /vendors/me/onboarding-state
 */
export const PatchVendorOnboardingStateSchema = z.object({
  welcomeDismissed: z.boolean().optional(),
  checklistDismissed: z.boolean().optional(),
  completedSteps: z.array(z.string()).optional(),
  startedAt: z.string().datetime().nullable().optional(),
  completedAt: z.string().datetime().nullable().optional(),
});

export type PatchVendorOnboardingState = z.infer<typeof PatchVendorOnboardingStateSchema>;
